import { useEffect, type RefObject } from 'react';

/**
 * Closes a popup when the pointer goes down outside it, or when Escape is pressed.
 *
 * Replaces the full-screen backdrop button the menus previously used: a backdrop swallows the
 * first click anywhere on the page and gives screen readers a control that does nothing. Listening
 * on `pointerdown` also closes before a click lands on whatever is underneath, which is what users
 * expect when dismissing a menu by clicking another control.
 */
export function useOutsideClick(
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
): void {
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && !ref.current?.contains(target)) onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [ref, isOpen, onClose]);
}
